/**
 * The artwork of a feed card: each kind's own icon, and the surface its tone washes into.
 * Kept apart from the card so the card file exports only the component.
 *
 * Ported from the workbench's components/home/feedArt.tsx: the console's own icons stand in for the desktop's
 * navigation icons and app icons.
 */
import { ArrowDownToLine, At, Bell, Briefcase, Clock, CodePullRequest, Cubes3, Database, Display, Eye, ListCheck, NodesRight, PaperPlane, Persons, Pulse, CircleQuestion } from '@gravity-ui/icons';
import type { ReactNode } from 'react';

import { type FeedKind } from '../../sharedHome.js';

/** The console's own icons for the areas the workbench's navigation names (its NAV_ICONS). */
const NAV_ICONS = {
  inbox: <Bell />,
  plans: <NodesRight />,
  facilitator: <Pulse />,
  decisions: <CircleQuestion />,
  risks: <Eye />,
  memory: <Database />,
  routines: <Clock />,
  timeline: <Clock />,
  tasks: <ListCheck />,
  teams: <Persons />,
  workspace: <Display />,
};

export const KIND_ICONS: Record<FeedKind, ReactNode> = {
  'needs-you': NAV_ICONS.inbox,
  plan: NAV_ICONS.plans,
  finished: NAV_ICONS.facilitator,
  decision: NAV_ICONS.decisions,
  watching: NAV_ICONS.risks,
  memory: NAV_ICONS.memory,
  routine: NAV_ICONS.routines,
  update: <ArrowDownToLine />,
  away: NAV_ICONS.timeline,
  'team-run': NAV_ICONS.facilitator,
  'team-task': NAV_ICONS.tasks,
  'team-change': NAV_ICONS.teams,
  'org-push': <Briefcase />,
  'team-node': NAV_ICONS.workspace,
  working: NAV_ICONS.workspace,
  work: NAV_ICONS.tasks,
  pr: <CodePullRequest />,
  dispatch: <PaperPlane />,
  app: <Cubes3 />,
  mention: <At />,
};

export function kindIcon(kind: FeedKind): ReactNode {
  return KIND_ICONS[kind];
}

/** A card's artwork: its kind's icon (an app's own icon is the desktop's; the console has no apps). */
export function itemIcon(item: { kind: FeedKind; icon?: string }): ReactNode {
  return KIND_ICONS[item.kind];
}

/** The card's surface: the tone washed into the theme's own surface, so light and dark both read. */
export function cardGradient(tone: string): string {
  return `linear-gradient(125deg, color-mix(in oklch, var(--tone-${tone}) 26%, var(--surface)), color-mix(in oklch, var(--tone-${tone}) 6%, var(--surface)) 70%)`;
}

